import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes } from 'react'
import { cn } from './cn'
import { Spinner } from './spinner'

/**
 * DESIGN.md component rules: gold with navy text for customer actions; the shape is the
 * surface's (--radius-control). `ui-btn` and `ui-btn-<variant>` style nothing here: they are
 * hooks for a surface's own motion (the website's fills, apps/booking/app/globals.css).
 */
export const buttonVariants = cva(
  'ui-btn inline-flex min-h-touch items-center justify-center gap-2 text-base font-bold no-underline transition-colors disabled:cursor-not-allowed disabled:opacity-60 aria-disabled:cursor-not-allowed aria-disabled:opacity-60',
  {
    variants: {
      variant: {
        /** Customer primary: gold pill, navy 700 text, yellow on hover. */
        primary: 'ui-btn-primary rounded-control bg-cta-bg px-6 text-cta-text hover:bg-cta-hover',
        /** Customer secondary: blue pill, white text. */
        secondary:
          'ui-btn-secondary rounded-control bg-brand px-6 text-action-text hover:bg-brand-strong',
        /** A quiet action next to a primary one. */
        outline:
          'ui-btn-outline rounded-control border border-text-muted bg-bg px-6 text-brand hover:border-brand hover:bg-surface',
        /** Staff primary: blue, 8 px corners; gold is never a staff action. */
        staff: 'ui-btn-staff rounded-sm bg-action-bg px-4 text-action-text hover:bg-brand-strong',
        /** Still 44 px tall: the touch target is the rule, whatever the look. */
        link: 'ui-btn-link px-0 font-medium text-link underline underline-offset-4 hover:text-brand-strong',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', block: false },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Shows a spinner, keeps the label and ignores further presses. The button
   * stays focusable (aria-disabled, not disabled), so focus is not lost while
   * the request runs.
   */
  busy?: boolean
}

export function Button({
  className,
  variant,
  block,
  busy = false,
  type = 'button',
  onClick,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(buttonVariants({ variant, block }), className)}
      aria-busy={busy || undefined}
      aria-disabled={busy || undefined}
      onClick={(event) => {
        // A busy submit button must not submit its form again.
        if (busy) {
          event.preventDefault()
          return
        }
        onClick?.(event)
      }}
      {...props}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  )
}
